import { IScreenCanvasSize, getReportCanvasSize } from "@kuika/kuika-cl-model";
import KuikaSkeleton from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import "./report-canvas-skeleton.scss";

/**
 * The report page, drawn as a placeholder while the real one is still loading.
 *
 * What it replaces: a full-viewport scrim with the product logo spinning in the middle of it.
 * The rulers and the design area around the page are drawn from the paper size immediately, so
 * that spinner sat inside finished chrome, over a page-shaped hole — it read as a hang rather
 * than as a page on its way. This previews the page instead: same sheet, same dotted grid, with
 * a few bars where content will land.
 *
 * Sized from the same derivation as the real page, so it does not jump when the report arrives.
 * Decorative, hence `aria-hidden`: the shell owns the one spoken "Loading project…".
 */
export interface IReportCanvasSkeletonProps {
  /** This screen's canvas-size record; absent on a cold load, which falls back to A4. */
  screenCanvasSize: IScreenCanvasSize | undefined;
}

export const ReportCanvasSkeleton = ({ screenCanvasSize }: IReportCanvasSkeletonProps): JSX.Element => {
  const { width, height } = getReportCanvasSize(screenCanvasSize);

  return (
    <div className="kuika_report-canvas-skeleton" aria-hidden="true" data-testid="reportCanvasSkeleton">
      <div className="kuika_report-canvas-skeleton__page" style={{ width: `${width}px`, height: `${height}px` }}>
        <div className="kuika_report-canvas-skeleton__band">
          <KuikaSkeleton width="45%" height={20} />
          <KuikaSkeleton width="28%" height={12} />
        </div>
        <div className="kuika_report-canvas-skeleton__band kuika_report-canvas-skeleton__band--body">
          <KuikaSkeleton width="100%" height={12} />
          <KuikaSkeleton width="92%" height={12} />
          <KuikaSkeleton width="96%" height={12} />
          <KuikaSkeleton width="60%" height={12} />
        </div>
      </div>
    </div>
  );
};
